import { blobEndpoint, datasetsContainer } from '~lib/storage/config';

// ABS REST API version. Bump deliberately — newer versions can change the XML
// schema. See: https://learn.microsoft.com/rest/api/storageservices/versioning-for-the-azure-storage-services
const ABS_API_VERSION = '2024-08-04';

export interface BlobEntry {
  name: string;
  contentLength: number;
  lastModified: string;
}

interface ListBlobsResult {
  blobs: BlobEntry[];
  // `BlobPrefix` names returned when a delimiter is supplied — i.e. the
  // "folders" one level below the prefix. Trailing delimiter is preserved
  // exactly as ABS returns it.
  prefixes: string[];
  nextMarker: string | null;
}

interface ListBlobsOptions {
  container: string;
  prefix?: string;
  marker?: string;
  delimiter?: string;
  maxResults?: number;
}

async function listBlobsPage(
  token: string,
  options: ListBlobsOptions,
): Promise<ListBlobsResult> {
  const url = new URL(`${blobEndpoint}/${options.container}`);
  url.searchParams.set('restype', 'container');
  url.searchParams.set('comp', 'list');
  if (options.prefix) url.searchParams.set('prefix', options.prefix);
  if (options.marker) url.searchParams.set('marker', options.marker);
  if (options.delimiter) url.searchParams.set('delimiter', options.delimiter);
  if (options.maxResults) {
    url.searchParams.set('maxresults', String(options.maxResults));
  }

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-ms-version': ABS_API_VERSION,
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `ABS list blobs failed: ${response.status} ${response.statusText}${
        body ? ` — ${body.slice(0, 200)}` : ''
      }`,
    );
  }

  const xml = await response.text();
  return parseListBlobsXml(xml);
}

// Walks the marker-paginated list endpoint until exhausted. ABS caps each
// response at ~5000 blobs; for a test-data container this typically completes
// in a single request.
export async function listAllBlobs(
  token: string,
  prefix?: string,
): Promise<BlobEntry[]> {
  const collected: BlobEntry[] = [];
  let marker: string | null = null;

  do {
    const page: ListBlobsResult = await listBlobsPage(token, {
      container: datasetsContainer,
      prefix,
      marker: marker ?? undefined,
    });
    collected.push(...page.blobs);
    marker = page.nextMarker;
  } while (marker);

  return collected;
}

// Lists the top-level "folders" in a container without enumerating their
// contents. Uses the `/` delimiter so ABS returns one `BlobPrefix` per
// first-level segment — O(1) requests regardless of how many (potentially
// enormous) blobs live underneath. The returned names have their trailing
// slash stripped (`CellDensities/` → `CellDensities`).
export async function listTopLevelFolders(
  token: string,
  container: string,
): Promise<string[]> {
  const collected: string[] = [];
  let marker: string | null = null;

  do {
    const page: ListBlobsResult = await listBlobsPage(token, {
      container,
      delimiter: '/',
      marker: marker ?? undefined,
    });
    collected.push(...page.prefixes);
    marker = page.nextMarker;
  } while (marker);

  return collected
    .map((prefix) => prefix.replace(/\/$/, ''))
    .filter((name) => name.length > 0)
    .sort((a, b) => a.localeCompare(b));
}

function parseListBlobsXml(xml: string): ListBlobsResult {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');

  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    throw new Error(`ABS response was not valid XML: ${parserError.textContent}`);
  }

  const blobs: BlobEntry[] = [];
  for (const blob of doc.querySelectorAll('Blobs > Blob')) {
    const name = blob.querySelector('Name')?.textContent ?? '';
    const contentLength = Number(
      blob.querySelector('Properties > Content-Length')?.textContent ?? '0',
    );
    const lastModified =
      blob.querySelector('Properties > Last-Modified')?.textContent ?? '';
    blobs.push({ name, contentLength, lastModified });
  }

  const prefixes: string[] = [];
  for (const prefix of doc.querySelectorAll('Blobs > BlobPrefix')) {
    const name = prefix.querySelector('Name')?.textContent ?? '';
    if (name) prefixes.push(name);
  }

  const nextMarkerText =
    doc.querySelector('EnumerationResults > NextMarker')?.textContent ?? '';
  const nextMarker = nextMarkerText.length > 0 ? nextMarkerText : null;

  return { blobs, prefixes, nextMarker };
}
